import { runWithErrorReporting } from "$root/api/error-reporting";
import { flattenProperties } from "$root/api/repository";
import { StackSidePanelContext, StackSidePanelOpenContext } from "$root/components/common/Stack/StackSidePanel";
import { Divider } from "$root/components/Divider";
import { IconWalls } from "$root/components/IconWall";
import { SortDropdown, type SortByOption, type SortDirection } from "$root/components/SortDropdown";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { useMappedQueries, useMappedQuery, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { ChartTitle, type ChartWallStatus } from "$root/ui-lib/charts/ChartCardShell";
import { FrequencyChartWall } from "$root/ui-lib/charts/FrequencyChart";
import { type MetricComparisonChartItem, MetricComparisonChartWall } from "$root/ui-lib/charts/MetricComparisonChart";
import { PerformanceLineChartWall } from "$root/ui-lib/charts/PerformanceLineChart";
import { type StackedMetricChartProps, StackedMetricChartWall } from "$root/ui-lib/charts/StackedMetricChart";
import { DensityChartWall } from "$root/ui-lib/charts/DensityChart";
import { filterMap, minutes } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import {
	type ConceptDefinitionDTO,
	type DataProviderRequestDTO,
	type DataResponseDTO,
	type ParameterizedConceptDTO,
	type RiskModelExposureLevels,
	ViewModeEnum,
} from "@eu.mdotm.api/smart-workspaces";
import type { ConceptBreakdownDTO, WorkspaceChartTypeEnum } from "../spec-gaps";
import { CircularProgressBar, Column, Icon, Row, Text } from "@mdotm/shadcn/legacy/components";
import { useDeepEqualMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { switchExpr, typedObjectEntries, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { DropdownMenu, type DropdownTriggerProps, Switch, Button } from "@mdotm/shadcn/mdotm/components";
import { ForEach, useMandatoryContext } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import BigNumber from "bignumber.js";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { HighchartsReactRefObject } from "highcharts-react-official";
import { Set } from "immutable";
import { type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { chartTypeSortingMode, timeSeriesFormOf } from "../chart-type";
import {
	InlineConceptChainRow,
	InlineConceptChainTitle,
	inlineChainCanBeLive,
	inlineChainIsLive,
	type InlineConceptPatch,
	riskModelConceptId,
} from "../chain";
import { autoGenerateMetricName, DEFAULT_CHART_LABEL } from "../common";
import { formFromConcept } from "../concept-form";
import type { ParameterizedConceptDTOWithMandatoryUuid, WorkspaceChartCtx } from "../ctx";
import {
	type ActionOf,
	type DataRequestTail,
	type DataRequestTarget,
	buildDataRequest,
	selectConcept,
	selectDataPredicate,
	selectNameConceptUuid,
	selectRequestConcepts,
	useWorkspaceDispatch,
	useWorkspaceSlices,
	useWorkspaceStore,
} from "../state";
import { createFilterFromParametrizedConcept } from "../filter-utils";
import { workspaceHandlers } from "../hooks";
import { isConceptCustomExposureTag, workspaceRepository } from "../repository";
import { extractDataProviderValue } from "../WorkspaceDataDefinitionCast";
import { WorkspacePortfolioBulkActions } from "../WorkspacePortfolioBulkActions";
import {
	dataProviderItemsToLineChartData,
	dataProviderItemsToStackedChartData,
	distributionResponseToFrequencyChartData,
	distributionResponseToDensityChartData,
	type DensityChartData,
	type FrequencyData,
	type LineChartData,
	normalCurvePoints,
	sortStackedChartData,
	type StackedChartData,
	stackTotalSortValue,
} from "./mappers";
import {
	type WorkspaceChartSettingsDetails,
	WorkspaceChartSettingsStack,
	WorkspaceCreateChartSettingsStack,
	WorkspaceEditChartSettingsStack,
} from "./WorkspaceChartSettings";

function WorkspaceChart() {
	const charts = useAtomValue(useWorkspaceSlices().charts$);
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const selectionChartHandler = workspaceHandlers().useSelectionChart();
	const visibleCharts = charts.filter((c) => !(c.preferences?.hide ?? false));

	if (visibleCharts.length === 0) {
		return (
			<Card className="border border-solid border-(--palette-N100)">
				<IconWalls.WorkspaceMissingConcepts
					onClick={() =>
						sidePanelCtx.push([
							WorkspaceChartSettingsStack,
							() => <WorkspaceCreateChartSettingsStack title="New chart" />,
						])
					}
					buttonContent={
						<>
							<Icon icon="Outline1" />
							Add chart
						</>
					}
				/>
			</Card>
		);
	}

	return (
		<>
			<Card className="border border-solid border-(--palette-N100)">
				<Column gap={12}>
					{/* Walks the full collection, not `visibleCharts`: `index` is the chart's position in the
					    persisted array, and every edit handler writes back through it. */}
					<ForEach
						collection={charts}
						// The chart type is part of the key on purpose: an item runs its type's hooks, so a chart
						// edited into another type has to remount rather than re-render against a different spec. It
						// used to remount because each type was a component of its own.
						keyProvider={(chart, index) => `chart-id-${index}-${chart.chartType}`}
					>
						{(chart, index) =>
							!chart.preferences?.hide && (
								<WorkspaceChartItem chart={chart} index={index} spec={chartTypeSpec[chart.chartType]} />
							)
						}
					</ForEach>
					<div className="h-14">
						<IconWalls.WorkspaceAddChart
							style={{ padding: 0 }}
							onClick={() =>
								sidePanelCtx.push([
									WorkspaceChartSettingsStack,
									() => <WorkspaceCreateChartSettingsStack title="New chart" />,
								])
							}
						/>
					</div>
				</Column>
			</Card>
			<WorkspacePortfolioBulkActions
				selection={selectionChartHandler.selection}
				onSelect={(newSelection) => selectionChartHandler.setSelection(newSelection)}
			/>
		</>
	);
}

/**
 * The stacked column's sort, which is its own: it orders the rows by one stacked segment (or by the stack
 * total) frontend-side, and never touches the workspace sort — see `chartTypeSortingMode`.
 */
type WorkspaceChartCategorySort = {
	/** What can be ordered by: the stack total, then the categories in legend order. */
	options: ReadonlyArray<SortByOption>;
	category: string | undefined;
	direction: SortDirection;
	onChange(next: { category: string | undefined; direction: SortDirection }): void;
};

type WorkspaceChartActionsProps = {
	index: number;
	chart: WorkspaceChartCtx;
	parametrizedConcepts: ParameterizedConceptDTOWithMandatoryUuid[];
	highlightProposal: boolean;
	onHighlightProposalChange: (value: boolean) => void;
	/** Whether there is a proposal to highlight at all — see {@link hasProposalToHighlight}. */
	hasProposal: boolean;
	concept: ConceptDefinitionDTO | undefined;
	currentParameterizedConceptUuid: string | undefined;
	proposalParameterizedConceptUuid: string | undefined;
	disableChartSorting?: boolean;
	/** Only the `CATEGORY`-sorting chart type (the stacked column) provides this. */
	categorySort?: WorkspaceChartCategorySort;
};

function WorkspaceChartMidActions(props: WorkspaceChartActionsProps) {
	const sortChartHandler = workspaceHandlers().useSortChart();
	const isMatching = sortChartHandler.sort?.parameterizedConceptUuid
		? props.chart.series.includes(sortChartHandler.sort.parameterizedConceptUuid)
		: false;
	const chartType = props.chart.chartType;

	// What ordering means is not shared across the chart types: the backend comparator for the column
	// chart, one stacked segment for the stacked column, and nothing where the X axis is bins or time.
	const sortingMode = chartTypeSortingMode(chartType);
	const showWorkspaceSorting = sortingMode === "WORKSPACE";
	const categorySort = sortingMode === "CATEGORY" ? props.categorySort : undefined;

	return (
		<Row minSize="0" gap={8} alignItems="center" className="shrink-0">
			{/* Rendered only where there is a proposal to highlight — a PROPOSAL series in the chart's
			    configuration and a portfolio in the predicate carrying a value for it. See
			    `hasProposalToHighlight`. */}
			{props.hasProposal && (
				<Switch
					checked={props.highlightProposal}
					onChange={(newHighlightProposal) => {
						props.onHighlightProposalChange(newHighlightProposal);
						// Which uuid the *workspace* sort points at follows the highlight, so the bars keep being
						// ordered by the series being read. Only this chart's own sort may be rewritten, and only when
						// it is the workspace one — a stacked column's sort is a frontend view over its own rows.
						if (
							showWorkspaceSorting &&
							sortChartHandler.sort?.parameterizedConceptUuid &&
							props.chart.series.includes(sortChartHandler.sort?.parameterizedConceptUuid)
						) {
							sortChartHandler.setSort(props.index, {
								direction: sortChartHandler.sort?.direction,
								parameterizedConceptUuid: newHighlightProposal
									? props.proposalParameterizedConceptUuid
									: props.currentParameterizedConceptUuid,
							});
						}
					}}
				>
					Highlight proposal
				</Switch>
			)}
			{props.hasProposal && (showWorkspaceSorting || categorySort) && (
				<Divider direction="vertical" className="h-[17px]" />
			)}

			{showWorkspaceSorting && (
				<SortDropdown
					disabled={props.disableChartSorting}
					direction={isMatching ? sortChartHandler.sort?.direction : undefined}
					onChange={(direction) =>
						sortChartHandler.setSort(props.index, {
							direction,
							parameterizedConceptUuid: direction
								? props.highlightProposal
									? props.proposalParameterizedConceptUuid
									: props.currentParameterizedConceptUuid
								: undefined,
						})
					}
				/>
			)}

			{categorySort && (
				<SortDropdown
					// Nothing to order by until the response declares its categories (loading, error, no data).
					disabled={categorySort.options.length === 0}
					direction={categorySort.direction}
					options={categorySort.options}
					selectedOption={categorySort.category ?? stackTotalSortValue}
					onChange={(direction, option) => categorySort.onChange({ direction, category: option || undefined })}
				/>
			)}

			{/* Closes the controls off against the overflow menu, which is a different kind of thing: these act on
			    what the chart draws, the menu on the chart itself. */}
			{(props.hasProposal || showWorkspaceSorting || categorySort) && (
				<Divider direction="vertical" className="h-[17px]" />
			)}
		</Row>
	);
}

function WorkspaceChartActions(props: WorkspaceChartActionsProps) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const { dispatch, readState } = useWorkspaceStore();
	// `chartHasSeries` and `conceptReferencesResolve` are both enforced, so a chart has a `series[0]` and the
	// pool holds it. Asserted rather than branched on: the "this chart is broken" state the branch existed for
	// is one no document can arrive in and no action can produce.
	const parametrizedConcept =
		useAtomValue(useWorkspaceSlices().concepts$).find(
			(concept) => concept.uuid === props.currentParameterizedConceptUuid,
		) ?? unreachable();

	// Not a resolution check, despite reading like one: `props.concept` is the metric's *definition*, looked up
	// in the `getConceptMapDefinitions` catalogue that this component reads without waiting for. Until it
	// answers there is no form to open on and no chain to hand a column or a filter, so the three
	// concept-dependent entries disable themselves and the menu stays reachable for "Delete chart".
	const definitionUnavailable = props.concept === undefined;

	return (
		<DropdownMenu
			actions={[
				[
					"",
					[
						{
							icon: "Edit",
							label: "Edit chart",
							disabled: definitionUnavailable,
							onClick: unpromisify(async () => {
								const conceptsDefinitionMap = await workspaceRepository.getConceptMapDefinitions.fetchQuery();
								const workspaceTagConcepts = await workspaceRepository.getTagConceptDefinitions.fetchQuery();

								sidePanelCtx.push([
									WorkspaceChartSettingsStack,
									() => (
										<WorkspaceEditChartSettingsStack
											title={props.chart.label ?? DEFAULT_CHART_LABEL}
											index={props.index}
											description={props.chart.description}
											initialData={props.parametrizedConcepts
												.map(
													(parametrizedConcept): Partial<WorkspaceChartSettingsDetails> =>
														formFromConcept(
															parametrizedConcept,
															conceptsDefinitionMap.get(parametrizedConcept.conceptId),
															{
																chart: props.chart,
																tagConcepts: workspaceTagConcepts,
																resolveConcept: (uuid) => selectConcept(readState(), uuid),
															},
														),
												)
												.at(0)}
										/>
									),
								]);
							}),
						},
						{
							icon: "Chart",
							label: "Chart settings",
							onClick: () => sidePanelCtx.push(WorkspaceChartSettingsStack),
						},
					],
				],
				[
					"",
					[
						{
							icon: "Table",
							label: "Create Column",
							onClickAsync: async () => {
								if (!props.concept) {
									unreachable();
								}

								await dispatch({
									type: "addColumn",
									chain: {
										concept: props.concept,
										name: parametrizedConcept.label,
										...parametrizedConcept,
									},
								});
								platformToast({
									children: (
										<ToastContent
											onClick={() => {
												void dispatch({ type: "setView", view: ViewModeEnum.Table });
												const agGrid = document.querySelector(
													'[data-qualifier="WorkspaceTable"] .ag-body-horizontal-scroll-viewport',
												);
												if (agGrid) {
													agGrid.scrollLeft = Number.MAX_SAFE_INTEGER;
												}
											}}
										>
											<Text type="Body/M/Bold">New column created</Text>
											<br />
											<Text type="Body/M/Book">Click to view</Text>
										</ToastContent>
									),
									icon: "Icon-full-ok",
									severity: "success",
								});
								void dispatch({ type: "setView", view: ViewModeEnum.Table });
							},
							disabled: definitionUnavailable,
						},
						{
							icon: "Filter-no",
							label: "Create Filter",
							onClickAsync: async () => {
								if (!props.concept) {
									unreachable();
								}
								const concept = props.concept;
								await runWithErrorReporting(
									() => createFilterFromParametrizedConcept(sidePanelCtx, concept, parametrizedConcept),
									{
										area: "portfolio",
										attemptedOperation: {
											message: "unable to create filter from parametrized concept",
											payload: JSON.stringify({
												concept,
												parametrizedConcept,
											}),
										},
									},
								);
							},
							disabled: definitionUnavailable,
						},
					],
				],
				[
					"",
					[
						{
							icon: "Delete",
							label: "Delete chart",
							"data-qualifier": "WorkspaceChart/Actions/Delete",
							onClick: () => void dispatch({ type: "removeChart", index: props.index }),
						},
					],
				],
			]}
			Trigger={EditChartTrigger}
		/>
	);
}

function EditChartTrigger({ state: _state, ...props }: DropdownTriggerProps) {
	return (
		<Button palette="tertiary" size="small" data-qualifier="WorkspaceChart/Actions" {...props}>
			<Icon icon="More" className="rotate-90" size={20} color={themeCSSVars.palette_N500} />
		</Button>
	);
}

/**
 * Everything the chart types need identically: which portfolios (predicate), which parameterized
 * concepts, and the chrome inputs.
 *
 * The positional `series[0]` = CURRENT / `series[1]` = PROPOSAL convention holds for every chart type —
 * current/proposal survive everywhere — so it is resolved once, here.
 */
function useWorkspaceChartCommon(chart: WorkspaceChartCtx) {
	const slices = useWorkspaceSlices();
	const workspace = useAtomValue(slices.dto$);
	const allParametrizedConcepts = useAtomValue(slices.concepts$);
	const view = useAtomValue(slices.view$);
	const requestState = useAtomValue(slices.requestState$);
	// Called for its effect and not for its value: it clears a chart's persisted direction when the workspace
	// sort moves off that chart's series, and every mounted item used to run it because every one of them
	// needed `sort` to trim the pool. The request reads the sort out of the store now, so the value is gone
	// but the effect still has to run wherever an item is mounted.
	workspaceHandlers().useSortChart();
	const workspaceConceptsMap = workspaceRepository.getConceptMapDefinitions.useQuery();
	const { ref, inView } = useInView();

	const chartSeriesConcepts = useDeepEqualMemo(
		() =>
			filterMap(allParametrizedConcepts, (parametrized) =>
				chart.series.includes(parametrized.uuid) ? [parametrized] : [],
			),
		[allParametrizedConcepts, chart.series],
	);

	const predicate = useMemo(() => selectDataPredicate(requestState), [requestState]);
	const filteredParameterizedConcepts = selectRequestConcepts(requestState, chart.series);

	const unit = useMappedQuery(
		(conceptDefinitionMap) => {
			const conceptId = chartSeriesConcepts.at(0)?.conceptId;
			if (!conceptId) {
				return undefined;
			}
			return conceptDefinitionMap.get(conceptId)?.unitOfMeasurement;
		},
		[chartSeriesConcepts],
		workspaceConceptsMap,
	);

	return {
		ref,
		inView,
		view,
		workspace,
		workspaceConceptsMap,
		chartSeriesConcepts,
		nameParametrizedConceptUuid: selectNameConceptUuid(requestState),
		filteredParameterizedConcepts,
		predicate,
		/**
		 * This chart's request, minus the tail its endpoint takes: the type supplies `limit`/`offset`/`sorting`
		 * or `binning`, and says whether the response has rows to name. Everything else about it — which
		 * concepts, which projection, which predicate, which provider — is the same derivation for all five.
		 */
		dataRequest<TTail extends DataRequestTail>(target: Pick<DataRequestTarget<TTail>, "named" | "extras">) {
			return buildDataRequest(requestState, { ...target, projection: chart.series, chart });
		},
		unit: unit.data,
		concept: chartSeriesConcepts.at(0)?.conceptId
			? workspaceConceptsMap.data?.get(chartSeriesConcepts.at(0)!.conceptId)
			: undefined,
		currentParameterizedConceptUuid: chart.series.at(0),
		proposalParameterizedConceptUuid: chart.series.at(1),
		enabled: filteredParameterizedConcepts.length > 0 && inView && view === "CHART",
	};
}

/** Everything {@link useWorkspaceChartCommon} resolves once, which every chart type's own hook builds on. */
type WorkspaceChartCommon = ReturnType<typeof useWorkspaceChartCommon>;

/** The two props the three chart types that draw one bar per portfolio take, and that nothing else does. */
type WorkspaceChartSelection = {
	selectedIds: Set<string>;
	onSelectionChange(selected: Set<string>): void;
};

/**
 * The workspace selection, as the walls take it.
 *
 * A hook of the *chart types* that select rather than of the skeleton, so that the two walls with nothing to
 * select — the z-score's density and the line's time axis — keep subscribing to nothing: an item that
 * re-renders on a selection it does not draw makes Highcharts redraw the chart underneath it.
 */
function useChartSelection(): WorkspaceChartSelection {
	const selectionChartHandler = workspaceHandlers().useSelectionChart();
	return {
		selectedIds: Set(selectionChartHandler.selection),
		onSelectionChange: (selected) => selectionChartHandler.setSelection(selected.toArray()),
	};
}

/**
 * Suspends a wall's band drag while another surface owns the pointer: an open side panel, or a view that
 * is not the chart one. A hook of the types that drag, for the same reason as {@link useChartSelection}.
 */
function useChartDisabled(common: Pick<WorkspaceChartCommon, "view">): boolean {
	const sidePanelOpen = useMandatoryContext(StackSidePanelOpenContext);
	return sidePanelOpen || common.view !== ViewModeEnum.Chart;
}

/**
 * Whether there is a proposal to highlight at all: a PROPOSAL series in the chart's *configuration* and a
 * portfolio that actually has a value for it. Both halves are needed — a chart can carry a PROPOSAL series
 * every portfolio in the predicate happens to lack a proposal for, and there is nothing to highlight then
 * either.
 *
 * `dataHasProposal` is `undefined` until the response lands, and on error, and that reads as "no": the
 * toggle arrives with the data it acts on rather than ahead of it. Refetches keep the previous data
 * (`keepPreviousData`), so a chart that is already drawn never returns to the unanswered state.
 *
 * Which response field answers it is the chart type's own question, so each type answers it in its own
 * hook and hands the answer back as {@link WorkspaceChartTypeData.hasProposal}.
 *
 * @internal — exported for tests
 */
export function hasProposalToHighlight(
	proposalParameterizedConceptUuid: string | undefined,
	dataHasProposal: boolean | undefined,
): boolean {
	return proposalParameterizedConceptUuid != null && (dataHasProposal ?? false);
}

/** The persisted `preferences.highlightProposal` toggle, defaulted in one place. */
function chartHighlightsProposal(chart: WorkspaceChartCtx): boolean {
	return chart.preferences?.highlightProposal ?? false;
}

/**
 * The density chart's two overlay toggles, defaulted in one place — and defaulted **on**, unlike
 * `highlightProposal` in the same bag: the chart drew both before the settings panel could ask, so an
 * absent preference has to keep meaning "drawn". `formFromConcept` applies the same rule on the form side.
 */
function densityOverlays(chart: WorkspaceChartCtx): { showMeanLines: boolean; showNormalFit: boolean } {
	return {
		showMeanLines: chart.preferences?.showMeanLines ?? true,
		showNormalFit: chart.preferences?.showNormalFit ?? true,
	};
}

/**
 * The persisted `preferences.highlightProposal` toggle, which every chart type honours, clamped to
 * `hasProposal`.
 *
 * Clamped *here* rather than in each chart because the highlight drives more than the plot: the column
 * chart's workspace sort follows it, and the stacked column orders its rows by the highlighted stack. The
 * charts already ignore `highlightSecondaryMetric` without a proposal, so leaving the clamp to them would
 * let a stale preference keep reordering the bars by a series nobody can see.
 *
 * The preference itself is never written back. The data moves under a workspace (a filter, a new proposal),
 * and the user's answer should survive a refetch that happened to come back proposal-less rather than be
 * silently erased by it.
 */
function useHighlightProposal(chart: WorkspaceChartCtx, index: number, hasProposal: boolean) {
	const dispatch = useWorkspaceDispatch();
	const highlightProposal = chartHighlightsProposal(chart) && hasProposal;
	const setHighlightProposal = useCallback(
		(newHighlightProposal: boolean) => {
			void dispatch({
				type: "editChart",
				index,
				concepts: [],
				chart: { preferences: { highlightProposal: newHighlightProposal } },
			});
		},
		[dispatch, index],
	);
	return [highlightProposal, setHighlightProposal] as const;
}

/**
 * The stacked column's own sort — which stacked segment, or the stack total, orders the portfolios.
 *
 * Deliberately *not* `useSortChart`: that one drives the single workspace sort, which the backend applies
 * to one parameterized concept and the table shows too. Neither is available here — `SortCriterionDTO`
 * cannot name a breakdown category and the concept declares `sortable = false` — so this stays a frontend
 * view over the rows already fetched, persisted per chart and read by `sortStackedChartData`.
 */
function useCategorySort(chart: WorkspaceChartCtx, index: number) {
	const dispatch = useWorkspaceDispatch();
	const value = useMemo(
		() => ({
			direction: chart.preferences?.sort ?? undefined,
			// `null` / absent means "the stack total", which the mapper models as no category.
			category: chart.preferences?.sortCategory ?? undefined,
		}),
		[chart.preferences?.sort, chart.preferences?.sortCategory],
	);
	const setValue = useCallback(
		(next: { direction: SortDirection; category: string | undefined }) => {
			void dispatch({
				type: "editChart",
				index,
				concepts: [],
				// Explicit `null`s, not absent keys: `patch` treats a missing key as "leave it alone", so
				// clearing the sort has to say so.
				chart: { preferences: { sort: next.direction ?? null, sortCategory: next.category ?? null } },
			});
		},
		[dispatch, index],
	);
	return [value, setValue] as const;
}

/**
 * The key-metric threshold as a single number, whichever of the three modes produced it (FR-8).
 *
 * Manual wins when it is set — the two are mutually exclusive by construction, the settings panel clearing
 * one as soon as the other is chosen — and the Portfolio / Reference modes are a second, tiny request for
 * the same concept resolved against that investment or reference instead of against the workspace.
 *
 * Shared by every chart type `chartTypeSupportsThreshold` accepts, because resolving the value is the same
 * work everywhere; what differs is only which axis draws it, and whether the line can be dragged. Takes
 * the `common` bag rather than calling {@link useWorkspaceChartCommon} itself: a second call would mint a
 * second `useInView` ref that is never attached, so the query would never be enabled.
 */
function useChartThreshold(
	chart: WorkspaceChartCtx,
	common: Pick<ReturnType<typeof useWorkspaceChartCommon>, "workspaceConceptsMap" | "inView" | "view">,
): number | undefined {
	const allParametrizedConcepts = useAtomValue(useWorkspaceSlices().concepts$);

	const candidateRelativeThresholdParametrizedConcept = chart.relativeThreshold
		? allParametrizedConcepts.find(
				(parametrized) =>
					parametrized.uuid === chart.relativeThreshold &&
					(parametrized.dataProvider?.dataProviderId === "reference" ||
						parametrized.dataProvider?.dataProviderId === "investment"),
			)
		: undefined;

	const thresholdValueQuery = useQueryNoRefetch(
		[
			...workspaceRepository.getWorkspaceData.fullQueryKey,
			"threshold",
			...flattenProperties(candidateRelativeThresholdParametrizedConcept),
			...flattenProperties(candidateRelativeThresholdParametrizedConcept?.dataProvider),
		],
		workspaceRepository.getWorkspaceData.getQueryConfig({
			concepts: candidateRelativeThresholdParametrizedConcept ? [candidateRelativeThresholdParametrizedConcept] : [],
			dataProvider: candidateRelativeThresholdParametrizedConcept?.dataProvider as DataProviderRequestDTO,
			projection: candidateRelativeThresholdParametrizedConcept?.uuid
				? [candidateRelativeThresholdParametrizedConcept?.uuid]
				: [],
			limit: 1,
			offset: 0,
		}).queryFn,
		{
			keepPreviousData: true,
			cacheTime: minutes(10),
			staleTime: minutes(10),
			enabled: Boolean(candidateRelativeThresholdParametrizedConcept?.uuid) && common.inView && common.view === "CHART",
		},
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <uuid is the unique identifier of parametrizedConcept>
	const relativeThresholdValue = useMappedQueries(
		([thresholdQueryResult, conceptsMap]) => {
			const keyMetricDictionary =
				thresholdQueryResult.items?.at(0)?.dictionary?.[candidateRelativeThresholdParametrizedConcept?.uuid ?? ""];
			if (!keyMetricDictionary || !candidateRelativeThresholdParametrizedConcept) {
				return;
			}
			const concept = conceptsMap.get(candidateRelativeThresholdParametrizedConcept.conceptId);
			if (!concept?.dataType) {
				return;
			}
			const value = extractDataProviderValue(keyMetricDictionary, concept.dataType).value;
			return (Number.isNaN(Number(value)) ? undefined : value) as number | undefined;
		},
		[candidateRelativeThresholdParametrizedConcept?.uuid],
		[thresholdValueQuery, common.workspaceConceptsMap],
	);

	return chart.manualThreshold ?? relativeThresholdValue.data;
}

/**
 * The `/distribution` request, which the frequency distribution and the z-score now share *by
 * construction* rather than by being written twice.
 *
 * They were two copies of one query — same endpoint, same binning, same key — so that react-query serves
 * both chart types from one cache entry and a workspace holding both charts of one metric issues a single
 * request. All the z-score adds is `statistics`, which that endpoint returns unconditionally; there is no
 * z-score-specific request field, and now no way for the two to disagree about what they binned.
 *
 * The bin *labels* come with it: they are the axis' formatting rather than part of the response, so the
 * two mappers take a formatter, and it is the same formatter for both.
 */
function useDistributionQuery(chart: WorkspaceChartCtx, common: WorkspaceChartCommon) {
	const { formatNumber } = useLocaleFormatters();

	const binning = useDeepEqualMemo(
		() => chart.distributionProperties ?? { mode: "AUTO" as const },
		[chart.distributionProperties],
	);

	const query = useQueryNoRefetch(
		[
			...workspaceRepository.getWorkspaceDataDistribution.fullQueryKey,
			...flattenProperties(common.filteredParameterizedConcepts),
			...flattenProperties(common.predicate),
			...flattenProperties(binning),
		],
		workspaceRepository.getWorkspaceDataDistribution.getQueryConfig(
			// `named: false`: bins are not rows, so there is nothing to label and the name concept has never
			// been sent to this endpoint.
			common.dataRequest({ named: false, extras: { binning } }),
		).queryFn,
		{
			keepPreviousData: true,
			cacheTime: minutes(10),
			staleTime: minutes(10),
			enabled: common.enabled,
		},
	);

	const unit = common.unit ?? "";
	const formatBinEdge = useCallback(
		(value: number) => `${formatNumber(value, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`,
		[formatNumber, unit],
	);

	return { query, formatBinEdge };
}

/** @internal — exported for tests */
export function queryStatus(query: { isLoading: boolean; isError: boolean }, hasData: boolean): ChartWallStatus {
	if (query.isLoading) {
		return "CALCULATING";
	}
	if (query.isError) {
		return "ERROR";
	}
	return hasData ? "SUCCESS" : "NO_DATA";
}

/**
 * # One chart-item skeleton
 *
 * The five chart types are one component. Everything an item does that is not "which request, which mapper,
 * which wall" is the same in all five and used to be written out five times: the common bag, the
 * highlight toggle, the actions and the eight props they are built from, the spinner, the
 * {@link queryStatus} call, and the eight wall props no chart type varies.
 *
 * What a chart type still owns is its {@link WorkspaceChartTypeSpec} — its request (two of the five hit
 * `/distribution`), its response mapping, and its wall. Those stay five things on purpose: the walls are
 * tuned to different axes, and two mappings that look alike are not one mapping.
 */
type WorkspaceChartTarget = { chart: WorkspaceChartCtx; index: number };

/** The subset of a react-query result the skeleton reads — the spinner and {@link queryStatus}, nothing more. */
type WorkspaceChartQueryState = { isLoading: boolean; isError: boolean; isFetching: boolean };

/**
 * What a chart type's hook hands back.
 *
 * `view` is opaque to the skeleton: whatever that type's `render` needs, hook results included, so a
 * threshold, a selection or a chart ref is resolved by the types that have one and never mentioned by the
 * skeleton. The two exceptions are the two fields the *shared* actions take from a single type.
 */
type WorkspaceChartTypeData<TView> = {
	query: WorkspaceChartQueryState;
	/** Whether the response has something to draw — rows, bins or lines: only the chart type can say. */
	hasData: boolean;
	/**
	 * Whether the response carries a proposal to highlight — see {@link hasProposalToHighlight}. Which field
	 * says so differs: four types read the flag their mapper carries, the column chart derives it from its
	 * rows.
	 */
	hasProposal: boolean;
	view: TView;
	/** The stacked column alone: what its mid-actions offer to order the rows by. */
	categorySort?: WorkspaceChartCategorySort;
	/** The column chart alone: no rows to order and no sort of its own to keep, so the dropdown greys out. */
	disableChartSorting?: boolean;
};

/**
 * The wall props the skeleton fills in, identical for all five chart types.
 *
 * `Pick`ed off one wall's props rather than restated: they are that wall's props, all five declare the
 * same eight, and the five spreads in the specs are what keeps that true.
 */
type WorkspaceChartWallChrome = { status: ChartWallStatus } & Pick<
	StackedMetricChartProps,
	| "chartTitle"
	| "mainMetricLabel"
	| "secondaryMetricLabel"
	| "highlightSecondaryMetric"
	| "unit"
	| "midActions"
	| "actions"
	| "chartHeight"
>;

/** What a chart type's `render` is handed besides its own `view`. */
type WorkspaceChartRenderProps = {
	wall: WorkspaceChartWallChrome;
	/**
	 * `chartLegend.visible`, defaulted. Taken by the three walls that draw a legend — the stacked column names
	 * its categories, the line its portfolios, the density its two series and its fitted-normal dash; see
	 * `chartTypeDrawsLegend` for why the other two draw none.
	 */
	showLegend: boolean;
	/** Points a tooltip's title at the portfolio, inside this workspace. Three of the five walls take it. */
	tooltipTitleHrefProvider(portfolioUid: string): string;
};

/**
 * One chart type's answers: its data, and its wall.
 *
 * `useData` is a hook and `render` is deliberately not. Every hook a type needs — its query, its mapping,
 * its threshold, its selection — belongs to `useData`, which leaves `render` a pure function of what it
 * was given and keeps the number of hooks an item runs a property of its chart type alone.
 *
 * Both are declared as methods so that a spec with a concrete `view` still satisfies
 * `WorkspaceChartTypeSpec<unknown>`: the skeleton passes the `view` straight from one to the other
 * without looking inside it, and each spec pins its own type, which is what keeps its two halves in step.
 */
type WorkspaceChartTypeSpec<TView> = {
	useData(target: WorkspaceChartTarget, common: WorkspaceChartCommon): WorkspaceChartTypeData<TView>;
	render(view: TView, props: WorkspaceChartRenderProps): ReactNode;
};

/**
 * The chrome, the actions and the query lifecycle every chart item shares; the chart type supplies the rest.
 *
 * The type's hook is called unconditionally here, so an item's hook order is its type's — which is why
 * the caller keys the item on the chart type, so that editing a chart into another type remounts it instead of
 * re-rendering it against a different spec.
 */
function WorkspaceChartItem(props: { chart: WorkspaceChartCtx; index: number; spec: WorkspaceChartTypeSpec<unknown> }) {
	const common = useWorkspaceChartCommon(props.chart);
	// After the chart type's own hook, not before it: whether there is a proposal to highlight is read off
	// the response, which is the type's answer — and the toggle is clamped to it.
	const typeData = props.spec.useData({ chart: props.chart, index: props.index }, common);
	const [highlightProposal, setHighlightProposal] = useHighlightProposal(
		props.chart,
		props.index,
		typeData.hasProposal,
	);

	const actionProps: WorkspaceChartActionsProps = {
		index: props.index,
		chart: props.chart,
		concept: common.concept,
		parametrizedConcepts: common.chartSeriesConcepts,
		highlightProposal,
		onHighlightProposalChange: setHighlightProposal,
		hasProposal: typeData.hasProposal,
		currentParameterizedConceptUuid: common.currentParameterizedConceptUuid,
		proposalParameterizedConceptUuid: common.proposalParameterizedConceptUuid,
		disableChartSorting: typeData.disableChartSorting,
		categorySort: typeData.categorySort,
	};

	return (
		<div className="h-96 relative" ref={common.ref}>
			{/* `isError` rather than `status !== "error"`: the same condition, said in the one field the
			    skeleton already needs from the query. */}
			{typeData.query.isFetching && !typeData.query.isError && <ChartFetchingSpinner />}
			{props.spec.render(typeData.view, {
				wall: {
					status: queryStatus(typeData.query, typeData.hasData),
					midActions: <WorkspaceChartMidActions {...actionProps} />,
					actions: <WorkspaceChartActions {...actionProps} />,
					chartTitle: props.chart.label ?? DEFAULT_CHART_LABEL,
					mainMetricLabel: "Current",
					secondaryMetricLabel: "Proposal",
					highlightSecondaryMetric: highlightProposal,
					unit: common.unit,
					chartHeight: "full",
				},
				showLegend: props.chart.chartLegend?.visible ?? true,
				tooltipTitleHrefProvider: (portfolioUid) =>
					typedUrlForRoute("PortfolioDetailsWithWorkspace", {
						portfolioUid,
						workspaceUid: common.workspace.uuid!,
					}),
			})}
		</div>
	);
}

// --- Column --------------------------------------------------------------------------------------

type ColumnChartView = {
	data: Array<MetricComparisonChartItem>;
	threshold: number | undefined;
	onThresholdChange(threshold: number): void;
	selection: WorkspaceChartSelection;
	disable: boolean;
	innerRef: RefObject<HighchartsReactRefObject | null>;
};

/**
 * Column: two bars per portfolio, ordered by the workspace sort.
 *
 * `MetricComparisonChart` is deliberately *not* generalized — its drag-threshold, rubber-band selection
 * and navigator logic is tuned to exactly two series over a portfolio X axis — and this is the only type
 * whose threshold line can be dragged.
 */
const columnChartSpec: WorkspaceChartTypeSpec<ColumnChartView> = {
	useData: useColumnChartData,
	render(view, props) {
		return (
			<MetricComparisonChartWall
				{...props.wall}
				{...view.selection}
				data={view.data}
				threshold={view.threshold}
				thresholdDragStep={0.01}
				onThresholdChange={view.onThresholdChange}
				disable={view.disable}
				// Required by the wall and never read by it: it derives its own Min / Max / Average header
				// from `data`.
				tooltipTitleHrefProvider={props.tooltipTitleHrefProvider}
				innerRef={view.innerRef}
			/>
		);
	},
};

function useColumnChartData(
	{ chart, index }: WorkspaceChartTarget,
	common: WorkspaceChartCommon,
): WorkspaceChartTypeData<ColumnChartView> {
	const sortChartHandler = workspaceHandlers().useSortChart();
	const dispatch = useWorkspaceDispatch();
	const chartRef = useRef<HighchartsReactRefObject | null>(null);
	const threshold = useChartThreshold(chart, common);
	const selection = useChartSelection();
	const disable = useChartDisabled(common);

	const chartDataQuery = useQueryNoRefetch(
		[
			...workspaceRepository.getWorkspaceData.fullQueryKey,
			"chart",
			...flattenProperties(common.filteredParameterizedConcepts),
			...flattenProperties(sortChartHandler.sort),
			...flattenProperties(common.predicate),
			common.nameParametrizedConceptUuid,
		],
		workspaceRepository.getWorkspaceData.getQueryConfig(
			common.dataRequest({
				extras: {
					limit: 1000, // TODO: how many?
					offset: 0,
					sorting: sortChartHandler.sort ? [sortChartHandler.sort] : undefined,
				},
			}),
		).queryFn,
		{
			keepPreviousData: true,
			cacheTime: minutes(10),
			staleTime: minutes(10),
			enabled: common.enabled,
		},
	);

	const mappedQuerySeries = useMappedQueries(
		([data, conceptDefinitionMap]) => dataProviderItemsToMetricsComparisonChartData(data, conceptDefinitionMap),
		[],
		[chartDataQuery, common.workspaceConceptsMap],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the reset follows how *many* rows there are, deliberately not which.
	useEffect(() => {
		if (chartRef.current) {
			chartRef.current.chart.zoomOut();
		}
	}, [mappedQuerySeries.data?.length]);

	// This mapper answers with rows rather than with a data object, so the column chart derives its own
	// "is there a proposal": a row whose PROPOSAL metric is missing contributes no second bar. The other
	// four types read the flag their mapper already carries.
	const dataHasProposal = useMemo(
		() => mappedQuerySeries.data?.some((item) => item.metrics[1] != null),
		[mappedQuerySeries.data],
	);

	return {
		query: chartDataQuery,
		hasData: (mappedQuerySeries.data?.length ?? 0) > 0,
		hasProposal: hasProposalToHighlight(common.proposalParameterizedConceptUuid, dataHasProposal),
		disableChartSorting:
			mappedQuerySeries.data?.length === 0 &&
			!chart.series.includes(sortChartHandler.sort?.parameterizedConceptUuid ?? ""),
		view: {
			data: mappedQuerySeries.data ?? [],
			threshold,
			selection,
			disable,
			innerRef: chartRef,
			// Drag-to-move stays the column chart's own: the wall's pixel math is its y-axis', and the two
			// distribution types draw the line on a *categorical* or density X axis where the same
			// arithmetic means nothing. See the note on `useChartThreshold`.
			onThresholdChange: (newThreshold) => {
				void dispatch({
					type: "editChart",
					index,
					concepts: [],
					chart: {
						...chart,
						relativeThreshold: undefined,
						manualThreshold: newThreshold,
					},
				});
			},
		},
	};
}

// --- Stacked column ------------------------------------------------------------------------------

type StackedColumnChartView = {
	/** Ordered by {@link sortStackedChartData}, which is a view over the rows and never a refetch. */
	sorted: StackedChartData | undefined;
	hasProposal: boolean;
	selection: WorkspaceChartSelection;
	disable: boolean;
};

/** Stacked column: one stack per portfolio, split by the breakdown dimension the chart persists. */
const stackedColumnChartSpec: WorkspaceChartTypeSpec<StackedColumnChartView> = {
	useData: useStackedColumnChartData,
	render(view, props) {
		return (
			<StackedMetricChartWall
				{...props.wall}
				{...view.selection}
				categories={view.sorted?.categories ?? []}
				data={view.sorted?.items ?? []}
				hasProposal={view.hasProposal}
				showLegend={props.showLegend}
				disable={view.disable}
				tooltipTitleHrefProvider={props.tooltipTitleHrefProvider}
			/>
		);
	},
};

function useStackedColumnChartData(
	{ chart, index }: WorkspaceChartTarget,
	common: WorkspaceChartCommon,
): WorkspaceChartTypeData<StackedColumnChartView> {
	const [categorySort, setCategorySort] = useCategorySort(chart, index);
	const selection = useChartSelection();
	const disable = useChartDisabled(common);

	// The breakdown is persisted on the chart and copied onto the concept at request time — by
	// `buildDataRequest`, off the chart type — which is how the backend models it too (one
	// `BreakdownRequestDTO` on both sides); see `withChartSeriesRequest` for why it stays off the
	// persisted concept.
	const breakdown = useDeepEqualMemo(
		(): ConceptBreakdownDTO | undefined => chart.breakdownProperties,
		[chart.breakdownProperties],
	);

	const request = common.dataRequest({ extras: { limit: 1000, offset: 0 } });

	const chartDataQuery = useQueryNoRefetch(
		[
			...workspaceRepository.getWorkspaceData.fullQueryKey,
			"stacked-chart",
			...flattenProperties(request.concepts),
			...flattenProperties(common.predicate),
		],
		workspaceRepository.getWorkspaceData.getQueryConfig(request).queryFn,
		{
			keepPreviousData: true,
			cacheTime: minutes(10),
			staleTime: minutes(10),
			// No breakdown on any charted concept means there is nothing to stack: the request would come
			// back as plain scalars and the chart would render an empty wall either way.
			enabled: common.enabled && breakdown != null,
		},
	);

	const mapped = useMappedQuery(
		(data) =>
			dataProviderItemsToStackedChartData(data, {
				currentUuid: common.currentParameterizedConceptUuid,
				proposalUuid: common.proposalParameterizedConceptUuid,
			}),
		[common.currentParameterizedConceptUuid, common.proposalParameterizedConceptUuid],
		chartDataQuery,
	);

	// Read rather than toggled here: the toggle itself is the skeleton's, and what this chart type needs it
	// for is which stack decides the order. Clamped the same way the skeleton clamps it, so a stale
	// preference cannot go on ordering the rows by a stack nobody can see.
	const hasProposal = hasProposalToHighlight(common.proposalParameterizedConceptUuid, mapped.data?.hasProposal);
	const highlightProposal = chartHighlightsProposal(chart) && hasProposal;

	// Ordering happens here rather than in the request — the backend cannot order a breakdown — so it is
	// a view over the mapped rows and changing it never refetches. The highlighted stack is the one
	// read, so it is the one that decides the order, exactly as the column chart's sort follows the
	// highlight.
	const sorted = useMemo(
		() => mapped.data && sortStackedChartData(mapped.data, { ...categorySort, source: highlightProposal ? 1 : 0 }),
		[mapped.data, categorySort, highlightProposal],
	);

	const sortOptions = useMemo(
		(): Array<SortByOption> => [
			{ value: stackTotalSortValue, label: "Total" },
			...(mapped.data?.categories ?? []).map((category) => ({ value: category, label: category })),
		],
		[mapped.data?.categories],
	);

	return {
		query: chartDataQuery,
		hasData: (mapped.data?.items.length ?? 0) > 0,
		hasProposal,
		categorySort: {
			// Nothing to order by until the response declares its categories (loading, error, no data).
			options: mapped.data ? sortOptions : [],
			category: categorySort.category,
			direction: categorySort.direction,
			onChange: setCategorySort,
		},
		view: {
			sorted,
			hasProposal: mapped.data?.hasProposal ?? false,
			selection,
			disable,
		},
	};
}

// --- Frequency -----------------------------------------------------------------------------------

type FrequencyChartView = {
	data: FrequencyData | undefined;
	threshold: number | undefined;
	hasProposal: boolean;
	selection: WorkspaceChartSelection;
	disable: boolean;
};

/** Frequency: bins on the X axis, counted. */
const frequencyChartSpec: WorkspaceChartTypeSpec<FrequencyChartView> = {
	useData: useFrequencyChartData,
	render(view, props) {
		return (
			<FrequencyChartWall
				{...props.wall}
				{...view.selection}
				bins={view.data?.bins ?? []}
				threshold={view.threshold}
				hasProposal={view.hasProposal}
				disable={view.disable}
			/>
		);
	},
};

function useFrequencyChartData(
	{ chart }: WorkspaceChartTarget,
	common: WorkspaceChartCommon,
): WorkspaceChartTypeData<FrequencyChartView> {
	const threshold = useChartThreshold(chart, common);
	const selection = useChartSelection();
	const disable = useChartDisabled(common);
	const { query, formatBinEdge } = useDistributionQuery(chart, common);

	const mapped = useMappedQuery(
		(data) =>
			distributionResponseToFrequencyChartData(
				data,
				{
					currentUuid: common.currentParameterizedConceptUuid,
					proposalUuid: common.proposalParameterizedConceptUuid,
				},
				formatBinEdge,
			),
		[common.currentParameterizedConceptUuid, common.proposalParameterizedConceptUuid, formatBinEdge],
		query,
	);

	const hasProposal = hasProposalToHighlight(common.proposalParameterizedConceptUuid, mapped.data?.hasProposal);

	return {
		query,
		hasData: (mapped.data?.bins.length ?? 0) > 0,
		hasProposal,
		view: {
			data: mapped.data,
			threshold,
			// The response's own flag, not the chart's configuration: a PROPOSAL series every portfolio lacks a
			// value for gives the wall nothing to draw as a second distribution.
			hasProposal: mapped.data?.hasProposal ?? false,
			selection,
			disable,
		},
	};
}

// --- Density -------------------------------------------------------------------------------------

/**
 * What the density header needs to edit the chart's metric inline. `undefined` for a concept with no
 * step to offer, and while the concept catalogue has not answered.
 */
type DensityInlineMetric = {
	/** Where the steps render: in the title when the label is the generated one, in a row under it otherwise. */
	mode: "title" | "row";
	chart: WorkspaceChartCtx;
	index: number;
	concept: ConceptDefinitionDTO;
	seriesConcepts: ParameterizedConceptDTOWithMandatoryUuid[];
	/** The catalogue lookup the label needs after a word has switched the series to another concept. */
	conceptById(conceptId: string): ConceptDefinitionDTO | undefined;
};

/**
 * The same test `formFromConcept` uses to seed `autoGenerateName`, asked of the chart's own label: a
 * label the user never typed is the generated one, and it is regenerated rather than left behind.
 */
function chartLabelIsAutoGenerated(
	chart: WorkspaceChartCtx,
	seriesConcept: ParameterizedConceptDTO,
	concept: ConceptDefinitionDTO | undefined,
): boolean {
	const breakdown = chart.breakdownProperties ? { dimension: chart.breakdownProperties.dimension } : undefined;
	return (
		autoGenerateMetricName(
			{ parametrizedConcept: { ...seriesConcept, breakdown }, concept },
			{ ignoreSource: true },
		) === (chart.label ?? seriesConcept.label)
	);
}

/**
 * The `editChart` an inline change dispatches: every series patched in place under its own uuid, and the
 * label regenerated — against the patch's own concept, which a tag word may have switched — only where
 * it was the generated one.
 *
 * @internal — exported for tests
 */
export function inlineMetricEditAction(
	target: Omit<DensityInlineMetric, "mode">,
	patch: InlineConceptPatch,
): ActionOf<"editChart"> {
	const { chart, index, concept, seriesConcepts, conceptById } = target;
	const wasAuto = seriesConcepts[0] ? chartLabelIsAutoGenerated(chart, seriesConcepts[0], concept) : false;
	// In `chart.series` order rather than pool order: the reducer rewrites `series` from the payload's
	// order, and `series[0]` = CURRENT is positional everywhere in the area.
	const concepts = chart.series.flatMap((uuid) => {
		const series = seriesConcepts.find((candidate) => candidate.uuid === uuid);
		return series ? [{ ...series, ...(patch as Partial<ParameterizedConceptDTO>) }] : [];
	});
	if (!wasAuto) {
		return { type: "editChart", index, concepts, chart: {} };
	}
	const next = patch.conceptId && patch.conceptId !== concept.id ? conceptById(patch.conceptId) : concept;
	const label = autoGenerateMetricName({ parametrizedConcept: concepts[0], concept: next }, { ignoreSource: true });
	return { type: "editChart", index, concepts: concepts.map((series) => ({ ...series, label })), chart: { label } };
}

/**
 * The connected half of the inline chain: the option lists each word needs, and the dispatch.
 *
 * A component rather than part of {@link useDensityChartData} because a chart type's `render` is
 * deliberately hook-free. Each query is gated on the step that reads it, so a chart fetches only the
 * lists its own controls offer.
 */
function DensityInlineMetricControl(props: DensityInlineMetric): React.JSX.Element | null {
	const dispatch = useWorkspaceDispatch();
	const series = props.seriesConcepts.at(0);
	const form = timeSeriesFormOf(props.concept, props.chart.chartType);
	// The query keys the Edit Chart panel already uses for these endpoints, so react-query serves both.
	const combinationsQuery = useQuery({
		...workspaceRepository.getConceptOptions.getQueryConfig(props.concept.id!),
		enabled: props.concept.id === riskModelConceptId,
	}) as UseQueryResult<RiskModelExposureLevels[]>;
	const tagConceptsQuery = useQuery({
		...workspaceRepository.getTagConceptDefinitions.getQueryConfig(),
		enabled: isConceptCustomExposureTag(series?.conceptId ?? ""),
	});
	if (!series) {
		return null;
	}

	const data = {
		combinations: combinationsQuery.data,
		tagConcepts: tagConceptsQuery.data,
	};
	const shared = {
		concept: props.concept,
		form,
		series,
		data,
		// Applied immediately, like the highlight toggle: the item's own fetching spinner is the feedback.
		onChange: (patch: InlineConceptPatch) => void dispatch(inlineMetricEditAction(props, patch)),
	};
	if (props.mode === "row") {
		return <InlineConceptChainRow {...shared} />;
	}
	return inlineChainIsLive(props.concept, form, series, data) ? (
		<InlineConceptChainTitle {...shared} />
	) : (
		// Nothing loaded yet, or nothing to click: the quoted title every other chart shows.
		<ChartTitle>{props.chart.label ?? DEFAULT_CHART_LABEL}</ChartTitle>
	);
}

type DensityChartView = {
	data: DensityChartData | undefined;
	/** Fitted-normal reference points per source, sampled over the binned range. */
	normalCurves: [Array<[number, number]>, Array<[number, number]> | null];
	threshold: number | undefined;
	hasProposal: boolean;
	/** The persisted overlay toggles — see {@link densityOverlays}. */
	showMeanLines: boolean;
	showNormalFit: boolean;
	/** The inline level picker this chart's header carries, or `undefined` — see {@link DensityInlineMetric}. */
	inlineMetric: DensityInlineMetric | undefined;
};

/**
 * Density: the same request as the frequency chart, read as a density.
 *
 * Neither the selection nor the `disable` gate is here, and that is not an omission: this wall has no band
 * drag and draws no portfolio, so it takes neither prop. That is also why both are hooks of the chart types
 * that need them rather than of the skeleton — a density item goes on subscribing to neither, and a
 * selection made in the chart above it does not make Highcharts redraw a density it cannot affect.
 */
const densityChartSpec: WorkspaceChartTypeSpec<DensityChartView> = {
	useData: useDensityChartData,
	render(view, props) {
		return (
			<DensityChartWall
				{...props.wall}
				chartTitle={
					view.inlineMetric?.mode === "title" ? (
						<DensityInlineMetricControl {...view.inlineMetric} />
					) : (
						props.wall.chartTitle
					)
				}
				subTitle={view.inlineMetric?.mode === "row" ? <DensityInlineMetricControl {...view.inlineMetric} /> : undefined}
				bins={view.data?.bins ?? []}
				stats={view.data?.stats ?? [{ mean: 0, standardDeviation: 0, count: 0 }, null]}
				normalCurves={view.normalCurves}
				threshold={view.threshold}
				hasProposal={view.hasProposal}
				showMeanLines={view.showMeanLines}
				showNormalFit={view.showNormalFit}
				showLegend={props.showLegend}
			/>
		);
	},
};

function useDensityChartData(
	{ chart, index }: WorkspaceChartTarget,
	common: WorkspaceChartCommon,
): WorkspaceChartTypeData<DensityChartView> {
	const threshold = useChartThreshold(chart, common);
	const { query, formatBinEdge } = useDistributionQuery(chart, common);

	const mapped = useMappedQuery(
		(data) =>
			distributionResponseToDensityChartData(
				data,
				{
					currentUuid: common.currentParameterizedConceptUuid,
					proposalUuid: common.proposalParameterizedConceptUuid,
				},
				formatBinEdge,
			),
		[common.currentParameterizedConceptUuid, common.proposalParameterizedConceptUuid, formatBinEdge],
		query,
	);

	// Sampled across the binned range rather than the axis extremes: a bell that runs past the last bin
	// would stretch the X axis into empty space the data never reached.
	const normalCurves = useMemo((): [Array<[number, number]>, Array<[number, number]> | null] => {
		const bins = mapped.data?.bins ?? [];
		if (bins.length === 0 || !mapped.data) {
			return [[], null];
		}
		const range = { from: bins[0].from, to: bins[bins.length - 1].to };
		return [
			normalCurvePoints(mapped.data.stats[0], range),
			mapped.data.stats[1] ? normalCurvePoints(mapped.data.stats[1], range) : null,
		];
	}, [mapped.data]);

	const firstSeries = common.chartSeriesConcepts.at(0);
	const inlineForm = timeSeriesFormOf(common.concept, chart.chartType);
	const inlineMetric: DensityInlineMetric | undefined =
		common.concept && firstSeries && inlineChainCanBeLive(common.concept, inlineForm, firstSeries)
			? {
					mode: chartLabelIsAutoGenerated(chart, firstSeries, common.concept) ? "title" : "row",
					chart,
					index,
					concept: common.concept,
					seriesConcepts: common.chartSeriesConcepts,
					conceptById: (conceptId) => common.workspaceConceptsMap.data?.get(conceptId),
				}
			: undefined;

	return {
		query,
		hasData: (mapped.data?.bins.length ?? 0) > 0,
		hasProposal: hasProposalToHighlight(common.proposalParameterizedConceptUuid, mapped.data?.hasProposal),
		view: {
			data: mapped.data,
			normalCurves,
			threshold,
			// See the frequency chart: the response's flag, not the chart's configuration.
			hasProposal: mapped.data?.hasProposal ?? false,
			...densityOverlays(chart),
			inlineMetric,
		},
	};
}

// --- Line ----------------------------------------------------------------------------------------

type LineChartView = { data: LineChartData | undefined };

/**
 * Line: time on the X axis, one line per portfolio, from a `TIME_SERIES` concept.
 *
 * The plainest of the five — no breakdown to copy onto the concept, no binning, no threshold, no
 * selection. What bounds the payload is the chain's time horizon, which the backend trims the series to;
 * the FE sends nothing extra for it.
 *
 * `timeSeries: true` reaches the chart's own series through the request rather than the persisted concept
 * — it is what makes the backend resolve the concept's history instead of its single value, without which
 * both series come back as one number each and the chart draws nothing. The chart type decides it, so
 * `buildDataRequest` applies it off `chart.chartType`; see `withChartSeriesRequest` for why it stays off
 * the pool. The stacked column asks for its breakdown the same way.
 */
const lineChartSpec: WorkspaceChartTypeSpec<LineChartView> = {
	useData: useLineChartData,
	render(view, props) {
		return (
			<PerformanceLineChartWall
				{...props.wall}
				data={view.data?.items ?? []}
				hasProposal={view.data?.hasProposal ?? false}
				showLegend={props.showLegend}
				tooltipTitleHrefProvider={props.tooltipTitleHrefProvider}
			/>
		);
	},
};

function useLineChartData(
	_target: WorkspaceChartTarget,
	common: WorkspaceChartCommon,
): WorkspaceChartTypeData<LineChartView> {
	const request = common.dataRequest({ extras: { limit: 1000, offset: 0 } });

	const chartDataQuery = useQueryNoRefetch(
		[
			...workspaceRepository.getWorkspaceData.fullQueryKey,
			"line-chart",
			...flattenProperties(request.concepts),
			...flattenProperties(common.predicate),
		],
		workspaceRepository.getWorkspaceData.getQueryConfig(request).queryFn,
		{
			keepPreviousData: true,
			cacheTime: minutes(10),
			staleTime: minutes(10),
			enabled: common.enabled,
		},
	);

	const mapped = useMappedQuery(
		(data) =>
			dataProviderItemsToLineChartData(data, {
				currentUuid: common.currentParameterizedConceptUuid,
				proposalUuid: common.proposalParameterizedConceptUuid,
			}),
		[common.currentParameterizedConceptUuid, common.proposalParameterizedConceptUuid],
		chartDataQuery,
	);

	return {
		query: chartDataQuery,
		hasData: (mapped.data?.items.length ?? 0) > 0,
		hasProposal: hasProposalToHighlight(common.proposalParameterizedConceptUuid, mapped.data?.hasProposal),
		view: { data: mapped.data },
	};
}

/**
 * One row per chart type, closed with `satisfies` so a sixth type cannot be half-drawn: the compiler names
 * the missing row, and the row names the two questions it has to answer.
 *
 * The type's *non-render* answers — how it orders, whether it carries a threshold, what request-time form
 * its concepts take — are `chart-type.ts`'s and stay there. That module is read by the store and by both
 * settings panels, so it has to keep its distance from components; this table is the same key answered on
 * the render side.
 */
const chartTypeSpec = {
	HISTOGRAM: columnChartSpec,
	STACKED_COLUMN: stackedColumnChartSpec,
	FREQUENCY: frequencyChartSpec,
	DENSITY: densityChartSpec,
	PERFORMANCE_LINE: lineChartSpec,
} satisfies Record<WorkspaceChartTypeEnum, WorkspaceChartTypeSpec<unknown>>;

function ChartFetchingSpinner() {
	return (
		<CircularProgressBar
			value="indeterminate"
			className="absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			outerDiameter={24}
			innerDiameter={20}
		/>
	);
}

/** @internal — exported for tests */
export function dataProviderItemsToMetricsComparisonChartData(
	dataProvider: DataResponseDTO,
	conceptDefinition: Immutable.Map<string, ConceptDefinitionDTO>,
): Array<MetricComparisonChartItem> {
	const conceptMap =
		dataProvider.concepts?.reduce<Record<string, ParameterizedConceptDTOWithMandatoryUuid>>((map, concept) => {
			if (concept.uuid) {
				map[concept.uuid] = { ...concept, uuid: concept.uuid };
				return map;
			}
			return map;
		}, {}) ?? {};

	return (
		dataProvider.items?.flatMap((item): MetricComparisonChartItem[] => {
			let metric: MetricComparisonChartItem | null = null;

			if (item.predefinedFields?.uuid) {
				metric = {
					id: item.predefinedFields.uuid,
					name: item.predefinedFields.name ?? "missing name",
					metrics: [null, null],
					missingReason: [null, null],
				};
			}

			typedObjectEntries(item.dictionary ?? {}).forEach(([conceptUuid, conceptResult]) => {
				const concept = conceptMap[conceptUuid];
				if (!concept.source) {
					return;
				}

				const definition = conceptDefinition.get(concept.conceptId);
				const sourceIndex = switchExpr(concept.source, {
					CURRENT: () => 0,
					PROPOSAL: () => 1,
				});

				if (metric?.metrics && definition?.dataType) {
					const extracted = extractDataProviderValue(conceptResult, definition.dataType);
					metric.metrics[sourceIndex] = metricValue(
						(extracted.value ?? {}) as object,
						definition?.numericDecimalPlaces ?? 2,
					);

					if (metric.missingReason) {
						metric.missingReason[sourceIndex] =
							extracted.message === "Proposal is missing" ? null : (extracted.message ?? null);
					}
				}
			});

			return metric ? [metric] : [];
		}) ?? []
	);
}

function metricValue(value: object, numericDecimalPlaces: number) {
	const castedValue = alwaysNumber(value, 0);
	if (castedValue == null) {
		return null;
	}

	return BigNumber(castedValue).decimalPlaces(numericDecimalPlaces).toNumber();
}

function alwaysNumber<T>(value: T, fallback: number): number | null {
	if (typeof value === "number") {
		return value;
	}

	if (typeof value === "string") {
		const stringToNumber = Number(value);
		if (typeof stringToNumber === "number" && !Number.isNaN(stringToNumber)) {
			return stringToNumber;
		}
	}

	if (typeof value === "object") {
		return null;
	}

	return fallback;
}

export default WorkspaceChart;
